import React, { useEffect, useRef, useState } from 'react';
import strings from './strings';

// The value is the existing "confidence" number (0-100). It is an equal-power
// crossfade: 0 = only my part, 50 = everyone at full, 100 = only the others.
const SNAPS = [
  { value: 0, label: strings.myPart },
  { value: 50, label: strings.everyone },
  { value: 100, label: strings.others }
];

const percentages = (value) => {
  const angle = (value / 100) * (Math.PI / 2);
  return {
    me: Math.round(100 * Math.cos(angle)),
    others: Math.round(100 * Math.sin(angle))
  };
};

/**
 * Full-width touch slider. `onChange` is called at most once per animation
 * frame while dragging (every call re-renders the whole player) and once more
 * when the drag ends. `compact` shrinks the label row for the practice strip.
 */
const BlendSlider = ({ value, onChange, disabled, pending, compact, onDisabledTap }) => {
  const [local, setLocal] = useState(value);
  const [dragging, setDragging] = useState(false);
  const latestRef = useRef(value);
  const frameRef = useRef(null);

  // Follow external changes (preset load, label tap) unless mid-drag
  useEffect(() => {
    if (!dragging) {
      setLocal(value);
      latestRef.current = value;
    }
  }, [value, dragging]);

  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  const handleInput = (e) => {
    const next = Number(e.target.value);
    latestRef.current = next;
    setLocal(next);
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        onChange(latestRef.current);
      });
    }
  };

  const endDrag = () => {
    if (!dragging) return;
    setDragging(false);
    cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    onChange(latestRef.current);
  };

  const snapTo = (snapValue) => {
    latestRef.current = snapValue;
    setLocal(snapValue);
    onChange(snapValue);
  };

  const { me, others } = percentages(local);

  // No "me" part yet, but a loading preset may still restore one: show a
  // dimmed slider rather than flashing the "Pick my part" prompt.
  if (disabled && pending) {
    return (
      <div className={`mps-blend pending ${compact ? 'compact' : ''}`} aria-hidden="true">
        <div className="mps-blend-track">
          <input type="range" min="0" max="100" value={local} readOnly className="mps-blend-input" tabIndex={-1} />
        </div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className={`mps-blend ${compact ? 'compact' : ''}`}>
        <button type="button" className="mps-blend-pick" onClick={onDisabledTap}>
          {strings.pickMyPart}
        </button>
      </div>
    );
  }

  return (
    <div className={`mps-blend ${compact ? 'compact' : ''}`}>
      <div className="mps-blend-labels">
        {SNAPS.map(snap => (
          <button
            key={snap.value}
            type="button"
            className={`mps-blend-label ${Math.abs(local - snap.value) < 5 ? 'active' : ''}`}
            onClick={() => snapTo(snap.value)}
          >
            {snap.label}
          </button>
        ))}
      </div>
      <div className="mps-blend-track">
        {dragging && compact && (
          <div className="mps-blend-bubble" style={{ left: `${local}%` }}>
            {strings.showMine} {me}%
          </div>
        )}
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={local}
          className="mps-blend-input"
          aria-label={strings.blendLabel}
          aria-valuetext={`${strings.showMine} ${me}%, ${strings.others.toLowerCase()} ${others}%`}
          onChange={handleInput}
          onPointerDown={() => setDragging(true)}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onBlur={endDrag}
        />
      </div>
    </div>
  );
};

export default BlendSlider;
